/**
 * In-browser signing helpers for the playgrounds. Secret keys passed here live only in
 * JavaScript memory for the duration of the call: never sent to Pryme, never persisted.
 */
import { HEADERS, TYP, type BindingPayload, type ResponseSignaturePayload } from '@pryme/protocol';
import { contentDigest, ed25519, isSecretKey, isoSeconds, jwsDecode, jwsVerify, parseServiceSecret, randomB64, signatureBase, utf8, type DecodedJws } from '@pryme/sdk';
import type { Service, ServiceKey } from './types';
import { apiOrigin } from './portal';

export interface PrymeKeyRef {
  keyId: string;
  publicKey: string;
}

/**
 * Pryme's verification keys for this service's channel (spec: pair A public keys): they verify Pryme's
 * responses. With `forKey` (a signing key id), only the one of that key's generation: exactly what a
 * backend on that secret key trusts (ADR-0013).
 */
export function prymeKeysOf(s: Service, forKey?: ServiceKey): PrymeKeyRef[] {
  return s.keys
    .filter((k) => k.direction === 'A' && k.status === 'active' && (!forKey || k.generation === forKey.generation))
    .map((k) => ({ keyId: k.id, publicKey: k.public_key }));
}

/** Pryme's attestation issuer keys: they verify identity bindings, whatever secret key a backend runs on (ADR-0014). */
export function issuerKeysOf(s: Service): PrymeKeyRef[] {
  return s.issuerKeys ?? [];
}

export type SecretMatch =
  | { ok: true; key: ServiceKey; publicKey: string; /** The raw Ed25519 signing secret to sign with. */ signingSecret: string }
  | { ok: false; reason: string; publicKey?: string };

/**
 * Accept the service secret key (`sk_test_…` / `sk_live_…`) or, for explicit setups, a raw signing secret.
 * Derive its public key and find the matching registered signing key.
 */
export function matchSecret(secret: string, keys: ServiceKey[], direction: 'B' = 'B'): SecretMatch {
  let s = secret.trim();
  if (!s) return { ok: false, reason: 'Paste the secret key.' };
  if (isSecretKey(s)) {
    try {
      s = parseServiceSecret(s).signingKey.secretKey;
    } catch (e) {
      return { ok: false, reason: (e as Error).message.replace(/^invalid Pryme secret key: /, 'Not a valid Pryme secret key: ') };
    }
  }
  let pub: string;
  try {
    pub = ed25519.publicFromSecret(s);
  } catch {
    return { ok: false, reason: 'Not a valid Pryme secret key (sk_test_… / sk_live_…).' };
  }
  const key = keys.find((k) => k.direction === direction && k.public_key === pub);
  if (!key) return { ok: false, reason: 'This secret does not match any signing key registered for this service.', publicKey: pub };
  if (key.status !== 'active') return { ok: false, reason: `Key ${key.id} is revoked; Pryme will reject signatures made with it.`, publicKey: pub };
  return { ok: true, key, publicKey: pub, signingSecret: s };
}

export interface SignedTrace {
  method: string;
  url: string;
  target: string;
  body: string;
  base: string;
  requestHeaders: Record<string, string>;
  status: number;
  ok: boolean;
  responseText: string;
  responseJson: unknown;
  responseSignature: {
    present: boolean;
    token?: string;
    decoded?: DecodedJws<ResponseSignaturePayload>;
    verifiedWithKid?: string;
    checks: { label: string; ok: boolean; detail?: string }[];
    valid: boolean;
  };
}

/**
 * Perform a Service-B-signed request and return the full trace, including the verification
 * of Pryme's Service-A response signature (same checks as the SDK's `call`).
 */
export async function signedServiceRequest(opts: {
  serviceId: string;
  keyId: string;
  secretKey: string;
  audience: string;
  path: string;
  body?: unknown;
  method?: string;
  prymeKeys: PrymeKeyRef[];
  /** Override the nonce (playground replay demo). */
  nonce?: string;
}): Promise<SignedTrace> {
  const method = (opts.method ?? 'POST').toUpperCase();
  const url = new URL(opts.path, apiOrigin());
  const target = url.pathname + url.search;
  const body = opts.body === undefined ? '' : JSON.stringify(opts.body);
  const nonce = opts.nonce ?? randomB64(18);
  const timestamp = isoSeconds();
  const digest = contentDigest(body);
  const base = signatureBase({
    principal: `service:${opts.serviceId}`, keyId: opts.keyId, method, audience: opts.audience,
    target, contentDigest: digest, timestamp, nonce,
  });
  const requestHeaders: Record<string, string> = {
    'content-type': 'application/json',
    [HEADERS.service]: opts.serviceId,
    [HEADERS.keyId]: opts.keyId,
    [HEADERS.timestamp]: timestamp,
    [HEADERS.nonce]: nonce,
    [HEADERS.digest]: digest,
    [HEADERS.signature]: `ed25519=${ed25519.sign(opts.secretKey.trim(), utf8(base))}`,
  };
  const res = await fetch(url.toString(), { method, headers: requestHeaders, body: method === 'GET' ? undefined : body, credentials: 'omit' });
  const responseText = await res.text();
  let responseJson: unknown = null;
  try {
    responseJson = responseText ? JSON.parse(responseText) : null;
  } catch {
    responseJson = responseText;
  }
  const token = res.headers.get(HEADERS.responseSignature) ?? undefined;
  const checks: SignedTrace['responseSignature']['checks'] = [];
  let decoded: DecodedJws<ResponseSignaturePayload> | undefined;
  let verifiedWithKid: string | undefined;
  if (token) {
    try {
      decoded = jwsDecode<ResponseSignaturePayload>(token);
    } catch {
      /* reported below */
    }
    const kid = decoded?.header.kid;
    const anchor = opts.prymeKeys.find((k) => k.keyId === kid);
    const v = anchor ? jwsVerify<ResponseSignaturePayload>(anchor.publicKey, token, TYP.response) : null;
    if (v) verifiedWithKid = anchor!.keyId;
    checks.push({ label: `Signed by a trusted Pryme verification key (kid ${kid ?? '?'})`, ok: !!v, detail: anchor ? undefined : 'kid not among this service\'s active Pryme verification keys' });
    checks.push({ label: `typ = ${TYP.response}`, ok: decoded?.header.typ === TYP.response });
    const p = v?.payload ?? decoded?.payload;
    checks.push({ label: 'service_id matches this service', ok: p?.service_id === opts.serviceId });
    checks.push({ label: 'request_nonce matches the nonce we sent', ok: p?.request_nonce === nonce });
    checks.push({ label: 'body_digest matches the response body', ok: p?.body_digest === contentDigest(responseText) });
    checks.push({ label: `status matches HTTP ${res.status}`, ok: p?.status === res.status });
  }
  return {
    method, url: url.toString(), target, body, base, requestHeaders, status: res.status, ok: res.ok, responseText, responseJson,
    responseSignature: { present: !!token, token, decoded, verifiedWithKid, checks, valid: !!token && checks.every((c) => c.ok) },
  };
}

/** Headers safe to display (the signature itself is fine to show; it is not a secret). */
export function displayHeaders(h: Record<string, string>) {
  return Object.entries(h).map(([k, v]) => `${k}: ${v}`).join('\n');
}

export interface JwsInspection<P> {
  token: string;
  decoded: DecodedJws<P> | null;
  verified: boolean;
  verifiedWith?: string;
  error?: string;
}

export function inspectJws<P>(token: string | undefined | null, keys: PrymeKeyRef[], typ: string): JwsInspection<P> | null {
  if (!token) return null;
  let decoded: DecodedJws<P> | null = null;
  try {
    decoded = jwsDecode<P>(token);
  } catch (e) {
    return { token, decoded: null, verified: false, error: (e as Error).message };
  }
  const exact = keys.filter((k) => k.keyId === decoded!.header.kid);
  for (const k of exact) {
    if (jwsVerify<P>(k.publicKey, token, typ)) return { token, decoded, verified: true, verifiedWith: k.keyId };
  }
  return { token, decoded, verified: false, error: exact.length ? 'signature or typ check failed' : `no trusted key with kid ${decoded.header.kid}` };
}

/** Identity status vocabulary surfaced to developers (spec §14). */
export function identityStatusFromBinding(b: Pick<BindingPayload, 'identity_status' | 'environment'> | undefined | null): 'active' | 'active_sandbox' | 'pending_verification' {
  if (!b || b.identity_status !== 'active') return 'pending_verification';
  return b.environment === 'production' ? 'active' : 'active_sandbox';
}
